import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  analytics, campaigns as campaignsApi, deliverability,
  type DashboardAnalytics, type Campaign, type DeliverabilityResponse,
} from "@/lib/api";
import { useAuth } from "@/hooks/useAuth";
import StatusBadge from "@/components/StatusBadge";
import Spinner from "@/components/Spinner";
import {
  Users, Mail, TrendingUp, MousePointerClick, Plus, ArrowRight,
  ShieldCheck, ShieldAlert, Flame, Target, Zap, BarChart3,
  ChevronRight, RefreshCw,
} from "lucide-react";
import { AreaChart, Area, XAxis, Tooltip, ResponsiveContainer } from "recharts";

// ── Metric card ──────────────────────────────────────────────────────────────
function MetricCard({
  label, value, sub, icon: Icon, accent, spark, onClick,
}: {
  label: string; value: string | number; sub?: string;
  icon: React.ElementType; accent: string;
  spark?: Array<{ v: number }>;
  onClick?: () => void;
}) {
  return (
    <div onClick={onClick} className={`bg-white rounded-2xl border border-black/[0.06] p-5 flex flex-col gap-3 hover:shadow-md hover:shadow-black/[0.04] transition-shadow ${onClick ? "cursor-pointer" : ""}`}>
      <div className="flex items-start justify-between">
        <div className={`p-2.5 rounded-xl ${accent}`}>
          <Icon size={18} />
        </div>
        {spark && spark.length > 1 && (
          <ResponsiveContainer width={60} height={30}>
            <AreaChart data={spark} margin={{ top: 2, right: 0, bottom: 0, left: 0 }}>
              <defs>
                <linearGradient id={`sg-${label}`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#FF5F45" stopOpacity={0.3} />
                  <stop offset="100%" stopColor="#FF5F45" stopOpacity={0} />
                </linearGradient>
              </defs>
              <Area type="monotone" dataKey="v" stroke="#FF5F45" strokeWidth={1.5}
                fill={`url(#sg-${label})`} dot={false} />
            </AreaChart>
          </ResponsiveContainer>
        )}
      </div>
      <div>
        <p className="text-2xl font-bold text-ink leading-none mb-1">{value}</p>
        <p className="text-xs font-semibold text-slate uppercase tracking-wider">{label}</p>
        {sub && <p className="text-xs text-slate/70 mt-1">{sub}</p>}
      </div>
    </div>
  );
}

// ── Quick-action card ─────────────────────────────────────────────────────────
function QuickCard({ to, icon: Icon, label, desc, accent }: {
  to: string; icon: React.ElementType; label: string; desc: string; accent: string;
}) {
  return (
    <Link to={to} className="group flex items-center gap-4 bg-white rounded-2xl border border-black/[0.06] p-4 hover:shadow-md hover:shadow-black/[0.04] hover:border-black/[0.1] transition-all">
      <div className={`p-3 rounded-xl flex-shrink-0 ${accent}`}>
        <Icon size={20} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-semibold text-ink text-sm">{label}</p>
        <p className="text-xs text-slate mt-0.5 truncate">{desc}</p>
      </div>
      <ChevronRight size={16} className="text-slate/40 group-hover:text-slate/70 group-hover:translate-x-0.5 transition-all flex-shrink-0" />
    </Link>
  );
}

// ── Onboarding checklist ──────────────────────────────────────────────────────
const CHECKLIST = [
  { label: "Add subscribers",    to: "/subscribers",   done: false },
  { label: "Create a campaign",  to: "/campaigns",     done: false },
  { label: "Set up SMTP",        to: "/settings",      done: false },
  { label: "Start warmup plan",  to: "/deliverability",done: false },
  { label: "Connect integration",to: "/integrations",  done: false },
];

function OnboardingCard({ hasSubs, hasCampaigns }: { hasSubs: boolean; hasCampaigns: boolean }) {
  const items = CHECKLIST.map((item, i) => ({
    ...item,
    done: i === 0 ? hasSubs : i === 1 ? hasCampaigns : false,
  }));
  const done = items.filter((i) => i.done).length;
  if (done >= 3) return null;
  return (
    <div className="bg-white rounded-2xl border border-black/[0.06] p-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="font-bold text-ink">Get started</h2>
          <p className="text-xs text-slate mt-0.5">{done} of {items.length} steps complete</p>
        </div>
        <div className="w-10 h-10 relative">
          <svg className="w-10 h-10 -rotate-90" viewBox="0 0 36 36">
            <circle cx="18" cy="18" r="15" fill="none" stroke="#F0EDE8" strokeWidth="3" />
            <circle cx="18" cy="18" r="15" fill="none" stroke="#FF5F45" strokeWidth="3"
              strokeDasharray={`${(done / items.length) * 94} 94`}
              strokeLinecap="round" />
          </svg>
          <span className="absolute inset-0 flex items-center justify-center text-[10px] font-bold text-ink">{done}/{items.length}</span>
        </div>
      </div>
      <div className="space-y-1.5">
        {items.map((item) => (
          <Link key={item.label} to={item.to}
            className="flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-black/[0.02] transition-colors group">
            <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
              item.done ? "bg-teal border-teal" : "border-black/[0.12] group-hover:border-coral/50"
            }`}>
              {item.done && <svg className="w-2.5 h-2.5 text-white" fill="none" viewBox="0 0 10 10"><path d="M1.5 5l2.5 2.5 4.5-4.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" /></svg>}
            </div>
            <span className={`text-sm flex-1 ${item.done ? "text-slate line-through" : "text-ink font-medium"}`}>{item.label}</span>
            {!item.done && <ChevronRight size={14} className="text-slate/30 group-hover:text-coral/50 transition-colors" />}
          </Link>
        ))}
      </div>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────
export default function DashboardPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData]       = useState<DashboardAnalytics | null>(null);
  const [camps, setCamps]     = useState<Campaign[]>([]);
  const [deliv, setDeliv]     = useState<DeliverabilityResponse | null>(null);
  const [ts, setTs]           = useState<Array<{ date: string; opens: number; clicks: number; v: number }>>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = async (quiet = false) => {
    if (!quiet) setLoading(true); else setRefreshing(true);
    try {
      const [dash, tsSeries, campsRes, delivRes] = await Promise.allSettled([
        analytics.dashboard(),
        analytics.timeseries(30),
        campaignsApi.list({ maxResults: 5 }),
        deliverability.stats(),
      ]);
      if (dash.status === "fulfilled")  setData(dash.value);
      if (tsSeries.status === "fulfilled") {
        const mapped = tsSeries.value.timeseries.map((d) => ({
          date:   new Date(d.date).toLocaleDateString("en-US", { month: "short", day: "numeric" }),
          opens:  parseInt(d.opens) || 0,
          clicks: parseInt(d.clicks) || 0,
          v:      parseInt(d.opens) || 0,
        }));
        setTs(mapped);
      }
      if (campsRes.status === "fulfilled") setCamps(campsRes.value.campaigns);
      if (delivRes.status === "fulfilled") setDeliv(delivRes.value);
    } finally { setLoading(false); setRefreshing(false); }
  };

  useEffect(() => { load(); }, []);

  if (loading) return <Spinner className="h-screen" />;

  const perf    = data?.performance;
  const subs    = data?.subscribers;
  const hasSubs = parseInt(subs?.total_subscribers || "0") > 0;
  const hasCamps= camps.length > 0;

  const repScore  = deliv?.totals?.reputationScore;
  const isListed  = deliv?.blacklist?.anyListed;

  // Sparkline data for subscriber card
  const subSpark = ts.slice(-7).map((d) => ({ v: d.opens }));

  return (
    <div>
      {/* Header */}
      <div className="sticky top-0 z-10 bg-paper/90 backdrop-blur-md border-b border-black/[0.06] px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
        <div>
          <h1 className="text-lg sm:text-xl font-bold text-ink">
            Good {new Date().getHours() < 12 ? "morning" : new Date().getHours() < 17 ? "afternoon" : "evening"}, {user?.name?.split(" ")[0]}.
          </h1>
          <p className="text-xs sm:text-sm text-slate mt-0.5">Here's what's happening with your email.</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => load(true)} disabled={refreshing}
            className="p-2.5 rounded-xl border border-black/[0.06] text-slate hover:text-ink hover:border-black/[0.12] transition-colors">
            <RefreshCw size={15} className={refreshing ? "animate-spin" : ""} />
          </button>
          <Link to="/campaigns/wizard"
            className="inline-flex items-center gap-2 px-4 sm:px-5 py-2.5 rounded-full bg-coral text-white text-sm font-semibold hover:bg-coral-dark transition-colors shadow-md shadow-coral/25">
            <Plus size={15} />
            <span className="hidden sm:inline">New Campaign</span>
            <span className="sm:hidden">New</span>
          </Link>
        </div>
      </div>

      <div className="p-4 sm:p-6 lg:p-8 space-y-6 max-w-7xl mx-auto">

        {/* Metric cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
          <MetricCard
            label="Active Subscribers"
            value={Number(subs?.active_subscribers || 0).toLocaleString()}
            sub={`+${subs?.new_subscribers_30d || 0} this month`}
            icon={Users} accent="bg-teal/10 text-teal"
            spark={subSpark}
            onClick={() => navigate("/engagement?type=subscribers")}
          />
          <MetricCard
            label="Emails Sent"
            value={Number(perf?.total_sent || 0).toLocaleString()}
            sub="All time"
            icon={Mail} accent="bg-coral/10 text-coral"
            onClick={() => navigate("/engagement?type=sent")}
          />
          <MetricCard
            label="Open Rate"
            value={`${perf?.open_rate || 0}%`}
            sub="30-day average"
            icon={TrendingUp} accent="bg-blue-50 text-blue-600"
            onClick={() => navigate("/engagement?type=opened")}
          />
          <MetricCard
            label="Click Rate"
            value={`${perf?.click_rate || 0}%`}
            sub="30-day average"
            icon={MousePointerClick} accent="bg-purple-50 text-purple-600"
            onClick={() => navigate("/engagement?type=clicked")}
          />
        </div>

        {/* Chart + Onboarding row */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 lg:gap-6">

          {/* Chart — takes 2/3 */}
          <div className="lg:col-span-2 bg-white rounded-2xl border border-black/[0.06] p-5 sm:p-6">
            <div className="flex items-center justify-between mb-5">
              <h2 className="font-bold text-ink">30-Day Engagement</h2>
              <Link to="/analytics" className="text-xs font-semibold text-coral hover:underline flex items-center gap-1">
                Full analytics <ArrowRight size={12} />
              </Link>
            </div>
            {ts.length > 0 ? (
              <ResponsiveContainer width="100%" height={200}>
                <AreaChart data={ts} margin={{ top: 4, right: 4, bottom: 0, left: -20 }}>
                  <defs>
                    <linearGradient id="gOpens" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#FF5F45" stopOpacity={0.18} />
                      <stop offset="100%" stopColor="#FF5F45" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="gClicks" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#0A8F7F" stopOpacity={0.18} />
                      <stop offset="100%" stopColor="#0A8F7F" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#5A6872" }} axisLine={false} tickLine={false} interval="preserveStartEnd" />
                  <Tooltip
                    contentStyle={{ background: "#0B1215", border: "none", borderRadius: 10, color: "white", fontSize: 12, padding: "8px 12px" }}
                    cursor={{ stroke: "#FF5F45", strokeWidth: 1, strokeDasharray: "4 4" }}
                  />
                  <Area type="monotone" dataKey="opens" stroke="#FF5F45" strokeWidth={2} fill="url(#gOpens)" name="Opens" dot={false} />
                  <Area type="monotone" dataKey="clicks" stroke="#0A8F7F" strokeWidth={2} fill="url(#gClicks)" name="Clicks" dot={false} />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[200px] flex flex-col items-center justify-center text-slate gap-3">
                <BarChart3 size={28} className="opacity-30" />
                <p className="text-sm">Send your first campaign to see engagement data</p>
              </div>
            )}
          </div>

          {/* Onboarding / Health sidebar */}
          <div className="space-y-4">
            {(!hasSubs || !hasCamps) && (
              <OnboardingCard hasSubs={hasSubs} hasCampaigns={hasCamps} />
            )}

            {/* Deliverability health mini-card */}
            <div className="bg-white rounded-2xl border border-black/[0.06] p-4 flex items-center gap-4">
              <div className={`p-3 rounded-xl flex-shrink-0 ${isListed ? "bg-red-50" : "bg-green-50"}`}>
                {isListed
                  ? <ShieldAlert size={20} className="text-red-500" />
                  : <ShieldCheck size={20} className="text-green-600" />
                }
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-ink">
                  {isListed ? "IP Blacklisted" : repScore ? `Score: ${repScore}/100` : "IP Health"}
                </p>
                <p className="text-xs text-slate mt-0.5 truncate">
                  {isListed ? "Action needed" : "Clean — not on any blacklist"}
                </p>
              </div>
              <Link to="/deliverability" className="p-1.5 rounded-lg text-slate/40 hover:text-coral transition-colors">
                <ChevronRight size={16} />
              </Link>
            </div>

            {/* Warmup mini-card */}
            {deliv?.warmup && (
              <div className="bg-white rounded-2xl border border-black/[0.06] p-4 flex items-center gap-4">
                <div className="p-3 rounded-xl bg-coral/10 flex-shrink-0">
                  <Flame size={20} className="text-coral" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-ink">Warming up</p>
                  <p className="text-xs text-slate mt-0.5">Day {deliv.warmup.day_number} · {deliv.warmup.emails_sent_today}/{deliv.warmup.daily_limit} today</p>
                </div>
                <Link to="/deliverability" className="p-1.5 rounded-lg text-slate/40 hover:text-coral transition-colors">
                  <ChevronRight size={16} />
                </Link>
              </div>
            )}
          </div>
        </div>

        {/* Quick actions */}
        <div>
          <h2 className="font-bold text-ink mb-3">Quick actions</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <QuickCard to="/campaigns/wizard" icon={Zap} label="AI Campaign Builder" desc="Write + send with AI" accent="bg-coral/10 text-coral" />
            <QuickCard to="/leads" icon={Target} label="Lead Finder" desc="Reddit intent leads" accent="bg-amber-50 text-amber-600" />
            <QuickCard to="/deliverability" icon={ShieldCheck} label="Deliverability" desc="IP health + warmup" accent="bg-green-50 text-green-600" />
            <QuickCard to="/analytics" icon={BarChart3} label="Analytics" desc="Open + click rates" accent="bg-blue-50 text-blue-600" />
          </div>
        </div>

        {/* Recent campaigns */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-bold text-ink">Recent campaigns</h2>
            <Link to="/campaigns" className="text-xs font-semibold text-coral hover:underline flex items-center gap-1">
              View all <ArrowRight size={12} />
            </Link>
          </div>

          <div className="bg-white rounded-2xl border border-black/[0.06] overflow-hidden">
            {/* Desktop table */}
            <div className="hidden sm:block overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-black/[0.06]">
                    {["Campaign", "Status", "Sent", "Open Rate", "Click Rate", "Date"].map((h) => (
                      <th key={h} className="px-5 py-3 text-left text-[11px] font-bold uppercase tracking-wider text-slate">
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {camps.map((c) => {
                    const sent   = Number(c.sent_count || 0);
                    const opens  = Number(c.open_count || 0);
                    const clicks = Number(c.click_count || 0);
                    return (
                      <tr key={c.id} className="border-b border-black/[0.04] hover:bg-black/[0.01] transition-colors">
                        <td className="px-5 py-3.5 font-semibold text-ink max-w-[200px] truncate">{c.name}</td>
                        <td className="px-5 py-3.5"><StatusBadge status={c.status} /></td>
                        <td className="px-5 py-3.5 text-slate">{sent > 0 ? sent.toLocaleString() : "—"}</td>
                        <td className="px-5 py-3.5 font-semibold text-ink">{sent > 0 ? `${((opens / sent) * 100).toFixed(1)}%` : "—"}</td>
                        <td className="px-5 py-3.5 text-slate">{sent > 0 ? `${((clicks / sent) * 100).toFixed(1)}%` : "—"}</td>
                        <td className="px-5 py-3.5 text-slate text-xs whitespace-nowrap">
                          {new Date(c.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                        </td>
                      </tr>
                    );
                  })}
                  {camps.length === 0 && (
                    <tr>
                      <td colSpan={6} className="px-5 py-14 text-center">
                        <div className="flex flex-col items-center gap-3">
                          <Mail size={28} className="text-slate opacity-30" />
                          <p className="text-slate text-sm">No campaigns yet.</p>
                          <Link to="/campaigns/wizard" className="text-coral text-sm font-semibold hover:underline">Create your first campaign →</Link>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Mobile card list */}
            <div className="sm:hidden divide-y divide-black/[0.04]">
              {camps.length === 0 ? (
                <div className="px-5 py-12 text-center">
                  <Mail size={24} className="text-slate opacity-30 mx-auto mb-3" />
                  <p className="text-slate text-sm">No campaigns yet.</p>
                  <Link to="/campaigns/wizard" className="text-coral text-sm font-semibold mt-2 block hover:underline">Create your first →</Link>
                </div>
              ) : camps.map((c) => {
                const sent  = Number(c.sent_count || 0);
                const opens = Number(c.open_count || 0);
                return (
                  <div key={c.id} className="px-4 py-3.5 flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-sm text-ink truncate">{c.name}</p>
                      <div className="flex items-center gap-3 mt-1">
                        <StatusBadge status={c.status} />
                        <span className="text-xs text-slate">
                          {new Date(c.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                        </span>
                      </div>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className="text-sm font-bold text-ink">
                        {sent > 0 ? `${((opens / sent) * 100).toFixed(1)}%` : "—"}
                      </p>
                      <p className="text-[11px] text-slate">open rate</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
